import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { useCoursePricing } from '@/hooks/useCoursePricing';
import { useLocation } from '@/contexts/LocationContext';
import {
  ArrowRight,
  ArrowLeft,
  Clock,
  DollarSign,
  Calendar,
  Users,
} from 'lucide-react';
import {
  COURSES,
  coursesForState,
  isSupportedProgramState,
  PROGRAMS_DEFAULT_STATE,
  STATE_FULL_NAME,
  type AustralianState,
  type CourseDefinition,
} from '@/data/courses/courses';
import {
  PROGRAM_CATEGORIES,
  categoryBlurbForState,
  YEAR_CHIPS,
  YEAR_FILTERS,
  parseYearRange,
  courseMatchesFilter,
  type YearFilterId,
} from '@/data/programs/categories';

const CONTACT_CENTRE_PRICE = 'Contact your local centre';
const PRIMARY_BORDER_COLOR = '#D85A30';

const STATE_META: Record<AustralianState, { title: string; description: string }> = {
  NSW: {
    title: 'Tutoring Programs in NSW | Schoolola',
    description:
      'NSW tutoring programmes for Kindergarten to Year 8: Foundations, OC TMRW™, Selective TMRW™, NAPLAN and OC-to-Selective bridging.', // NSW state-scoped — keep "Kindergarten"
  },
  VIC: {
    title: 'Tutoring Programs in Victoria | Schoolola',
    description:
      'Victoria tutoring programmes for Prep to Year 8: Foundations, Scholarship Exam (SEC), VSEC, NAPLAN and After School support.',
  },
  QLD: { title: 'Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  WA: {
    title: 'Tutoring Programs in Western Australia | Schoolola',
    description:
      'Tutoring in Western Australia for Pre-primary to Year 8. GATE test preparation and NAPLAN preparation. Book a free assessment.',
  },
  SA: { title: 'Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  NT: { title: 'Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  TAS: { title: 'Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  ACT: { title: 'Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
};

const ALL_STATE_META: Record<AustralianState, { title: string; description: string }> = {
  NSW: {
    title: 'All Tutoring Programs in New South Wales | Schoolola',
    description:
      'Full list of Schoolola tutoring programmes in NSW: Foundations, OC TMRW™, Selective TMRW™, OC-to-Selective bridging and NAPLAN.',
  },
  VIC: {
    title: 'All Tutoring Programs in Victoria | Schoolola',
    description:
      'Full list of Schoolola tutoring programmes in Victoria: Foundations, Scholarship Exam (SEC), VSEC, NAPLAN and After School support.',
  },
  QLD: { title: 'All Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  WA: {
    title: 'All Tutoring Programs in Western Australia | Schoolola',
    description:
      'Tutoring in Western Australia for Pre-primary to Year 8. GATE test preparation and NAPLAN preparation. Book a free assessment.',
  },
  SA: { title: 'All Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  NT: { title: 'All Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  TAS: { title: 'All Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
  ACT: { title: 'All Tutoring Programs | Schoolola', description: 'Schoolola tutoring programmes.' },
};

// -----------------------------------------------------------------------------
// Pricing - reuses the Phase 2 useCoursePricing hook.
// -----------------------------------------------------------------------------

const formatPriceFromRows = (rows: { price_per_session: number }[] | undefined) => {
  if (!rows || rows.length === 0) return null;
  const min = Math.min(...rows.map((r) => r.price_per_session));
  return `From $${min}/session`;
};

const useAllProgramsPricing = (
  state: AustralianState,
  centerUuid: string | undefined,
): Record<string, string | null> => {
  const map: Record<string, string | null> = {};
  for (const course of COURSES) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { pricing } = useCoursePricing(state, course.pricingCourseType, centerUuid);
    map[course.id] = formatPriceFromRows(pricing);

    if (course.testPack) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { pricing: tpPricing } = useCoursePricing(
        state,
        course.testPack.pricingCourseType,
        centerUuid,
      );
      map[`${course.id}__testpack`] = formatPriceFromRows(tpPricing);
    }
  }
  return map;
};

// -----------------------------------------------------------------------------
// Year chip grid (always expanded, 9 columns: Prep, Y1..Y8)
// -----------------------------------------------------------------------------

type YearChipGridProps = {
  state: AustralianState;
  courseId: string;
  activeYears: Set<number>;
};

const YearChipGrid = ({ state, courseId, activeYears }: YearChipGridProps) => (
  <div
    className="grid grid-cols-9 gap-1.5"
    role="list"
    aria-label="Year levels"
  >
    {YEAR_CHIPS.map((chip) => {
      const isActive = activeYears.has(chip.value);
      const baseClass =
        'flex items-center justify-center rounded-md text-[11px] md:text-xs font-medium py-1.5 border transition-colors';
      if (isActive) {
        return (
          <Link
            key={chip.value}
            to={`/programs/${state.toLowerCase()}/${courseId}#year-${chip.value}`}
            role="listitem"
            className={`${baseClass} bg-primary/10 text-primary border-primary/30 hover:bg-primary/20`}
            aria-label={`${chip.label} - jump to details`}
          >
            {state === 'WA' && chip.value === 0 ? 'Pre-primary' : chip.label}
          </Link>
        );
      }
      return (
        <span
          key={chip.value}
          role="listitem"
          aria-disabled="true"
          className={`${baseClass} bg-gray-50 text-gray-500 border-gray-200 cursor-not-allowed`}
          style={{ opacity: 0.25 }}
        >
          {state === 'WA' && chip.value === 0 ? 'Pre-primary' : chip.label}
        </span>
      );
    })}
  </div>
);

// -----------------------------------------------------------------------------
// Programme card
// -----------------------------------------------------------------------------

type ProgramCardProps = {
  state: AustralianState;
  course: CourseDefinition;
  priceDisplay: string;
  testPackPrice?: string;
  fadedByFilter?: boolean;
};

const ProgramCard = ({
  state,
  course,
  priceDisplay,
  testPackPrice,
  fadedByFilter = false,
}: ProgramCardProps) => {
  const badgeLabel = course.badges?.[0]
    ? course.badges[0] === 'most_popular'
      ? 'Most popular'
      : course.badges[0] === 'new'
      ? 'New'
      : course.badges[0] === 'high_value'
      ? 'High value'
      : 'Popular'
    : null;

  const activeYears = useMemo(() => parseYearRange(course.yearRange), [course.yearRange]);

  const baseClass = 'bg-white rounded-2xl p-6 md:p-7 shadow-sm transition-opacity';
  const borderStyle = course.primary
    ? { border: `2px solid ${PRIMARY_BORDER_COLOR}` }
    : undefined;
  const borderClass = course.primary ? '' : 'border border-gray-200';

  return (
    <article
      className={`${baseClass} ${borderClass}`}
      style={{
        ...(borderStyle ?? {}),
        opacity: fadedByFilter ? 0.35 : 1,
      }}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-2 mb-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
          <h3 className="text-base md:text-lg font-medium text-gray-900 leading-snug">
            {course.name}
          </h3>
          {badgeLabel && (
            <span className="inline-block text-[11px] uppercase tracking-wide font-medium px-2.5 py-1 rounded-md bg-primary/10 text-primary">
              {badgeLabel}
            </span>
          )}
        </div>
        <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
          {priceDisplay}
        </span>
      </header>

      {course.subtitle && (
        <p className="text-sm text-gray-500 mb-4">{course.subtitle}</p>
      )}

      <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4 text-[13px] text-gray-600">
        <span className="inline-flex items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
          {course.yearRange}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" aria-hidden="true" />
          {course.duration}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" aria-hidden="true" />
          {course.classSize}
        </span>
      </div>

      <div className="mb-4">
        <YearChipGrid state={state} courseId={course.id} activeYears={activeYears} />
      </div>

      <div className="mb-4">
        <h4 className="text-xs uppercase tracking-wide font-semibold text-gray-500 mb-2">
          What's involved
        </h4>
        <ul className="space-y-1.5">
          {course.features.map((feature, idx) => (
            <li
              key={idx}
              className="text-[13px] text-gray-600 leading-relaxed pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-primary"
            >
              {feature}
            </li>
          ))}
        </ul>
      </div>

      {course.testPack && (
        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-100">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-gray-500 mb-2">
            Also available: test pack
          </div>
          <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900">{course.testPack.name}</div>
              <p className="text-[13px] text-gray-600 leading-relaxed mt-0.5">
                {course.testPack.description}
              </p>
            </div>
            <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
              {testPackPrice ?? CONTACT_CENTRE_PRICE}
            </span>
          </div>
        </div>
      )}

      <div className="pt-1">
        <Link
          to={course.href}
          className="text-sm font-medium text-primary hover:underline"
        >
          Or read the full course overview →
        </Link>
      </div>
    </article>
  );
};

// -----------------------------------------------------------------------------
// All-programs flat list (only used by the /programs/:state/all route)
// -----------------------------------------------------------------------------

type AllProgramsListProps = {
  state: AustralianState;
  courses: CourseDefinition[];
  priceMap: Record<string, string | null>;
};

const AllProgramsList = ({ state, courses, priceMap }: AllProgramsListProps) => (
  <section id="all-programs" className="scroll-mt-24">
    <div className="mb-6">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-gray-900">
        All programs in {STATE_FULL_NAME[state]}
      </h2>
      <p className="text-gray-600 mt-2">
        The full catalogue, in case you already know what you're looking for.
      </p>
    </div>
    <div className="grid md:grid-cols-2 gap-5">
      {courses.map((course) => (
        <ProgramCard
          key={course.id}
          state={state}
          course={course}
          priceDisplay={priceMap[course.id] ?? CONTACT_CENTRE_PRICE}
          testPackPrice={
            course.testPack
              ? priceMap[`${course.id}__testpack`] ?? undefined
              : undefined
          }
        />
      ))}
    </div>
  </section>
);

// -----------------------------------------------------------------------------
// Redirect handler used by the bare `/programs` route.
// -----------------------------------------------------------------------------

export const ProgramsIndexRedirect = () => {
  const { selectedState } = useLocation();
  const target = isSupportedProgramState(selectedState)
    ? (selectedState.toUpperCase() as AustralianState)
    : PROGRAMS_DEFAULT_STATE;
  return <Navigate to={`/programs/${target.toLowerCase()}`} replace />;
};

// -----------------------------------------------------------------------------
// Categorised catalogue (Phase 4)
// -----------------------------------------------------------------------------

type CategorisedCatalogueProps = {
  state: AustralianState;
  courses: CourseDefinition[];
  priceMap: Record<string, string | null>;
  activeFilter: YearFilterId;
};

const CategorisedCatalogue = ({
  state,
  courses,
  priceMap,
  activeFilter,
}: CategorisedCatalogueProps) => {
  const coursesById = useMemo(() => {
    const m: Record<string, CourseDefinition> = {};
    for (const c of courses) m[c.id] = c;
    return m;
  }, [courses]);

  const filterDef = YEAR_FILTERS.find((f) => f.id === activeFilter) ?? YEAR_FILTERS[0];

  return (
    <div className="space-y-14">
      {PROGRAM_CATEGORIES.map((category) => {
        const categoryCourses = category.courseIds
          .map((id) => coursesById[id])
          .filter((c): c is CourseDefinition => Boolean(c));

        if (categoryCourses.length === 0) return null;

        return (
          <section key={category.id} aria-labelledby={`cat-${category.id}`}>
            <div className="mb-5">
              <h2
                id={`cat-${category.id}`}
                className="text-2xl md:text-3xl font-heading font-bold text-gray-900"
              >
                {category.title}
              </h2>
              <p className="text-gray-600 mt-1.5 max-w-2xl">{categoryBlurbForState(category, state)}</p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {categoryCourses.map((course) => {
                const courseYears = parseYearRange(course.yearRange);
                const matches =
                  activeFilter === 'all' ||
                  courseMatchesFilter(courseYears, filterDef.years);
                return (
                  <ProgramCard
                    key={course.id}
                    state={state}
                    course={course}
                    priceDisplay={priceMap[course.id] ?? CONTACT_CENTRE_PRICE}
                    testPackPrice={
                      course.testPack
                        ? priceMap[`${course.id}__testpack`] ?? undefined
                        : undefined
                    }
                    fadedByFilter={!matches}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Main Programs page - categorised catalogue (Phase 4 rebuild).
// -----------------------------------------------------------------------------

const ProgramsInner = ({ state }: { state: AustralianState }) => {
  const {
    selectedCenterId,
    selectedCenter,
    currentStateConfig,
    selectedState,
    setSelectedState,
  } = useLocation();

  useEffect(() => {
    if (selectedState !== state) setSelectedState(state);
  }, [state, selectedState, setSelectedState]);

  const courses = useMemo(() => coursesForState(state), [state]);
  const meta = STATE_META[state];
  const canonical = `https://schoolola.com.au/programs/${state.toLowerCase()}`;

  const activeCenter = useMemo(() => {
    if (!selectedCenter || !currentStateConfig) return null;
    if (currentStateConfig.code !== state) return null;
    return currentStateConfig.centers.find((c) => c.slug === selectedCenter) ?? null;
  }, [selectedCenter, currentStateConfig, state]);

  const priceMap = useAllProgramsPricing(
    state,
    activeCenter ? selectedCenterId ?? undefined : undefined,
  );

  const indicatorText = activeCenter
    ? `Showing prices for ${activeCenter.name}`
    : `Showing typical ${STATE_FULL_NAME[state]} pricing`;

  const [activeFilter, setActiveFilter] = useState<YearFilterId>('all');

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <div className="min-h-screen bg-white">
        <Header />

        {/* Pricing indicator (Phase 2) */}
        <div className="bg-white border-b border-gray-100 pt-20">
          <div className="container mx-auto px-4 py-4 flex justify-center">
            <span
              className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-4 py-1.5"
              aria-live="polite"
            >
              <DollarSign className="h-3.5 w-3.5 text-primary" />
              {indicatorText}
            </span>
          </div>
        </div>

        <section className="container mx-auto px-4 py-10 md:py-12">
          <div className="max-w-5xl mx-auto">
            {/* Page heading */}
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-gray-900">
                Programs in {STATE_FULL_NAME[state]}
              </h1>
              <p className="text-gray-600 mt-2 max-w-2xl">
                Browse every Schoolola programme available in {STATE_FULL_NAME[state]},
                grouped by what they help your child achieve.
              </p>
            </div>

            {/* Year-range filter chips */}
            <div
              className="mb-10 flex flex-wrap items-center gap-2"
              role="radiogroup"
              aria-label="Filter by year range"
            >
              <span className="text-xs uppercase tracking-wide text-gray-500 mr-2">
                Year range:
              </span>
              {YEAR_FILTERS.map((filter) => {
                const isActive = activeFilter === filter.id;
                return (
                  <button
                    key={filter.id}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => setActiveFilter(filter.id)}
                    className={`text-sm font-medium px-3.5 py-1.5 rounded-full border transition-colors ${
                      isActive
                        ? 'bg-primary text-white border-primary'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {state === 'WA' && filter.id === 'prep-y2' ? 'Pre-primary to Year 2' : filter.label}
                  </button>
                );
              })}
            </div>

            {/* Categorised catalogue */}
            <CategorisedCatalogue
              state={state}
              courses={courses}
              priceMap={priceMap}
              activeFilter={activeFilter}
            />

            {/* Soft assessment CTA */}
            <div className="mt-16 rounded-2xl border border-gray-200 bg-gray-50 px-6 py-8 md:px-10 md:py-10 text-center">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-gray-900">
                Not sure which programme is right?
              </h2>
              <p className="text-gray-600 mt-2 max-w-xl mx-auto">
                Book a free assessment and we'll recommend the best fit based on your
                child's current level and goals.
              </p>
              <div className="mt-5">
                <Link to="/book-assessment?source=programs-catalogue">
                  <Button
                    variant="outline"
                    className="rounded-xl font-semibold border-primary text-primary hover:bg-primary hover:text-white"
                  >
                    Book a free assessment
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Escape hatch: flat list */}
            <div className="mt-6 text-center">
              <Link
                to={`/programs/${state.toLowerCase()}/all`}
                className="text-sm font-medium text-primary hover:underline"
              >
                Prefer a flat list? See every program for {STATE_FULL_NAME[state]} →
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

const Programs = () => {
  const { state: stateParam } = useParams<{ state: string }>();
  if (!isSupportedProgramState(stateParam)) {
    return <Navigate to={`/programs/${PROGRAMS_DEFAULT_STATE.toLowerCase()}`} replace />;
  }
  const state = stateParam.toUpperCase() as AustralianState;
  return <ProgramsInner state={state} />;
};

// -----------------------------------------------------------------------------
// /programs/:state/all - standalone flat-list view
// -----------------------------------------------------------------------------

const ProgramsAllInner = ({ state }: { state: AustralianState }) => {
  const { selectedCenterId, selectedCenter, currentStateConfig, selectedState, setSelectedState } =
    useLocation();

  useEffect(() => {
    if (selectedState !== state) setSelectedState(state);
  }, [state, selectedState, setSelectedState]);

  const courses = useMemo(() => coursesForState(state), [state]);
  const meta = ALL_STATE_META[state];
  const canonical = `https://schoolola.com.au/programs/${state.toLowerCase()}/all`;

  const activeCenter = useMemo(() => {
    if (!selectedCenter || !currentStateConfig) return null;
    if (currentStateConfig.code !== state) return null;
    return currentStateConfig.centers.find((c) => c.slug === selectedCenter) ?? null;
  }, [selectedCenter, currentStateConfig, state]);

  const priceMap = useAllProgramsPricing(
    state,
    activeCenter ? selectedCenterId ?? undefined : undefined,
  );

  const indicatorText = activeCenter
    ? `Showing prices for ${activeCenter.name}`
    : `Showing typical ${STATE_FULL_NAME[state]} pricing`;

  return (
    <>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        <div className="bg-white border-b border-gray-100 pt-20">
          <div className="container mx-auto px-4 py-4 flex justify-center">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-4 py-1.5">
              <DollarSign className="h-3.5 w-3.5 text-primary" />
              {indicatorText}
            </span>
          </div>
        </div>
        <section className="container mx-auto px-4 py-12">
          <div className="max-w-5xl mx-auto">
            <div className="mb-3">
              <Link
                to={`/programs/${state.toLowerCase()}`}
                className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1.5"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to programs
              </Link>
            </div>
            <AllProgramsList state={state} courses={courses} priceMap={priceMap} />
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export const ProgramsAll = () => {
  const { state: stateParam } = useParams<{ state: string }>();
  if (!isSupportedProgramState(stateParam)) {
    return <Navigate to={`/programs/${PROGRAMS_DEFAULT_STATE.toLowerCase()}/all`} replace />;
  }
  const state = stateParam.toUpperCase() as AustralianState;
  return <ProgramsAllInner state={state} />;
};

export default Programs;
